import { Injectable } from '@angular/core';
import { environment as env } from '../../environments/environment';
import { Store } from '../store';
import { filter, map, tap } from 'rxjs/operators';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { BehaviorSubject } from 'rxjs';

declare var StripeTerminal: any;

@Injectable({
    providedIn: 'root'
})
export class PaymentsService
{
    private _terminal: BehaviorSubject<null | any> = new BehaviorSubject(null);
    private stripeTerminalData: any = null;

    constructor(
        private http: ApiAuthHttpClient,
        private store: Store,
    )
    {
        setTimeout(this.checkForTerminal.bind(this), 400);
    }

    // Wait for the async-loaded js.stripe.com/terminal global before use. Resolves
    // instantly once loaded; here it always is (runs after a network round-trip).
    private _whenStripeTerminalReady(timeoutMs = 10000): Promise<void> {
        return new Promise((resolve, reject) => {
            if (typeof StripeTerminal !== 'undefined') {
                resolve();
                return;
            }
            const start = Date.now();
            const interval = setInterval(() => {
                if (typeof StripeTerminal !== 'undefined') {
                    clearInterval(interval);
                    resolve();
                } else if (Date.now() - start > timeoutMs) {
                    clearInterval(interval);
                    reject(new Error('Stripe Terminal failed to load'));
                }
            }, 50);
        });
    }

    checkForTerminal(): void
    {
        this.http
            .get(`${env.apiPath}payments/getTerminalConnectionToken`)
            .pipe(
                filter(data => data !== false),
                map((data: any) => {
                    this.stripeTerminalData = data.data;
                    return this.stripeTerminalData;
                }),
                tap(async ({ token, location, simulated }) =>
                    { // docs https://stripe.com/docs/terminal/payments/connect-reader?terminal-sdk-platform=js&reader-type=internet
                        // js.stripe.com/terminal is loaded `async` in index.html; wait for the
                        // global before use (effectively instant here — this runs after a network
                        // round-trip, long after boot).
                        await this._whenStripeTerminalReady();
                        const terminal = StripeTerminal.create({
                            onFetchConnectionToken: () => token,
                            onUnexpectedReaderDisconnect: this.terminalError.bind(this),
                        });
                        await this.discoverReaders(terminal, { location, simulated });
                    }
                )
            ).subscribe();
    }

    private async discoverReaders(terminal, { location, simulated })
    {
        const discoverResult = await terminal.discoverReaders({ simulated, location });
        if (discoverResult.error) {
            // console.log('Failed to discover: ', discoverResult.error);
        } else if (discoverResult.discoveredReaders.length === 0) {
            console.error('No available readers.');
        } else {
            const connectResult = await terminal.connectReader(discoverResult.discoveredReaders[0]);
            if (connectResult.error) {
                console.error('Failed to connect:', connectResult.error);
            } else {
                this._terminal.next(terminal);
            }
        }
    }

    get terminal(): BehaviorSubject<null | any>
    {
        return this._terminal;
    }

    private async terminalError() {
        const terminal = this._terminal.value;
        this._terminal.next(null);
        await this.discoverReaders(terminal, this.stripeTerminalData);
    }

    intentCreate(intent, basket?)
    {
        const dataToSend = {
            intent,
            basket,
        };

        return this.http
            .post(env.apiPath + 'payments/intent', dataToSend)
            .pipe(map((data: any) => data));
    }

    attachPaymentMethodToCustomer(paymentMethodId, user)
    {

        const dataToSend = {
            paymentMethodId,
            userId: user.id,
            stripeId: user.stripeId
        };

        return this.http
            .post(env.apiPath + 'payments/method/attach', dataToSend)
            .pipe(map((data: any) => data));
    }

    cards(userId)
    {
        return this.http
            .get(env.apiPath + 'payments/cards/' + userId)
            .pipe(map((data: any) => data));
    }

    card(id)
    {

        return this.http
            .get(env.apiPath + 'payments/card/' + id)
            .pipe(map((data: any) => data));
    }


    tokenCreate(card)
    {
        const dataToSend = {
            card,
        };

        return this.http
            .post(env.apiPath + 'payments/token', dataToSend)
            .pipe(map((data: any) => data));
    }


    search(params)
    {
        const dataToSend = {
            params,
        };
        return this.http
            .post(env.apiPath + 'payments/admin', dataToSend)
            .pipe(map((data: any) => data));
    }

    setStatus(payment)
    {
        const user = this.store.selectForLocal('user');
        const dataToSend = {
            payment,
            user
        };
        return this.http
            .put(env.apiPath + 'payments', dataToSend)
            .pipe(map((data: any) => data));
    }
}
